// Баталгаажуулалт амжилттай дууслаа [successpage, error page]

'use client'
import Image from 'next/image'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useIntl } from '@/utils/intl'
import React, { FunctionComponent, useState } from 'react'
import { validate } from '@/services/mobi-api/confirmation'
import { getInstanceV2 } from '@/services/mobi-api/numberv2'
import { useRouter } from 'next/navigation'
import Loader from '@/components/ui/loader'
import { useAuth } from '@/context/AuthContext'
import { useToast } from '@/context/ToastContext'
import { useLanguage } from '@/context/LanguageContext'
import {
  normalizeTrackingText,
  trackNewNumberEvent
} from '@/utils/newNumberFlowTracking'

interface Props {
  onBack: () => void
  instanceId?: string
  regno?: string
  service?: 'digiConsent' | 'newNumber' | 'packageShift' | 'handset' | 'simRecovery'
  onComplete?: (instance?: any) => void
}

const Step5: FunctionComponent<Props> = ({
  instanceId,
  regno,
  service,
  onComplete
}) => {
  const { translate } = useIntl()
  const router = useRouter()
  const [loading, setloading] = useState(false)
  const { isWebView } = useAuth()
  const { showError } = useToast()
  const { locale } = useLanguage()

  const handleContinue = async (): Promise<void> => {
    if (service === 'digiConsent') {
      window.location.href = '/confirmation?digiConsent'
      try {
        if (isWebView) {
          const data = {
            key: 'digiConsent',
            value: {
              regno
            }
          }
          let webView
          if ((window as any).mobiBridge !== undefined) {
            webView = (window as any).mobiBridge
          }
          if ((window as any).ReactNativeWebview !== undefined) {
            webView = (window as any).ReactNativeWebview
          }
          if (webView !== undefined) {
            webView.postMessage(JSON.stringify(data))
          }
        }
      } catch (e) {
        console.error(e)
      }
      return
    }

    const hasInstanceId = typeof instanceId === 'string' && instanceId.trim().length > 0
    const hasRegno = typeof regno === 'string' && regno.trim().length > 0

    if (!hasInstanceId || !hasRegno) {
      showError(translate('error'))
      return
    }

    try {
      setloading(true)
      const validateResponse = await validate({ instanceId, regno })
      if (validateResponse != null) {
        const { _id, action } = validateResponse
        if (_id !== undefined && _id !== '' && action !== undefined && action !== '') {
          if (service === 'newNumber') {
            trackNewNumberEvent('verification_success', {
              step_name: 'identity_verification',
              method: 'emongolia'
            })
            trackNewNumberEvent('step_complete', {
              step_name: 'identity_verification'
            })
            const fullInstance = await getInstanceV2(_id, locale)
            setloading(false)
            onComplete?.(fullInstance ?? validateResponse)
            return
          }
          router.push(`/dan/redirect?sessionId=${_id}&action=${action}`)
        } else {
          if (service === 'newNumber') {
            trackNewNumberEvent('verification_fail', {
              step_name: 'identity_verification',
              method: 'emongolia',
              error_reason: normalizeTrackingText(validateResponse?.cause?.messageMn ?? 'validation_failed')
            })
          }
          if ((validateResponse?.cause?.messageMn) != null) {
            showError(validateResponse?.cause?.messageMn)
          } else {
            showError(translate('error'))
          }
          setloading(false)
          console.error('Validation failed')
        }
      } else {
        if (service === 'newNumber') {
          trackNewNumberEvent('verification_fail', {
            step_name: 'identity_verification',
            method: 'emongolia',
            error_reason: 'validation_failed'
          })
        }
        showError(translate('error'))
        setloading(false)
        console.error('Validation failed')
      }
    } catch (error) {
      if (service === 'newNumber') {
        trackNewNumberEvent('verification_fail', {
          step_name: 'identity_verification',
          method: 'emongolia',
          error_reason: 'validation_failed'
        })
      }
      showError(translate('error'))
      console.error('Error during validation:', error)
    } finally {
      setloading(false)
    }
  }

  return (
    <div
      className='w-full md:h-[75vh] h-screen flex flex-col md:justify-center items-center p-4'
      aria-busy={loading}
    >
      {loading && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm'>
          <Loader />
        </div>
      )}

      <div className={`rounded-3xl ${loading ? 'pointer-events-none opacity-60' : ''} space-y-4`}>
        <div className='md:bg-surfaceInverse md:p-4 rounded-3xl mt-16'>
          <Image
            src='/icons/success_alert.svg'
            width={100}
            height={100}
            className='mx-auto mb-4'
            alt='success alert'
            draggable={false}
          />
          <h1 className='text-center text-textHigh font-bold text-xl'>
            {translate('verify.success.info')}
          </h1>

          <div
            className='
              fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+16px)] z-50
              md:static md:inset-auto md:bottom-auto md:pt-4 md:w-[calc(100%-32px)] md:mx-auto
            '
          >
            <DarkButton
              classNames='w-full'
              onClick={() => { void handleContinue() }}
              disabled={loading}
            >
              {translate('continue')}
            </DarkButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Step5
